import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-2940">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2941">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-2942">
          <ArrowLeftIcon size={20} data-id="element-2943" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2944">
        <div className="flex items-center justify-between mb-8" data-id="element-2945">
          <div className="flex-1" data-id="element-2946">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2947">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2948">
                1
              </div>
              <div className="ml-3" data-id="element-2949">
                <div className="text-sm font-medium" data-id="element-2950">Bill Details</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2951"></div>
          <div className="flex-1" data-id="element-2952">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2953">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2954">
                2
              </div>
              <div className="ml-3" data-id="element-2955">
                <div className="text-sm font-medium" data-id="element-2956">Select Provider</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2957"></div>
          <div className="flex-1" data-id="element-2958">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2959">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2960">
                3
              </div>
              <div className="ml-3" data-id="element-2961">
                <div className="text-sm font-medium" data-id="element-2962">Financing Terms</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2963"></div>
          <div className="flex-1" data-id="element-2964">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2965">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2966">
                4
              </div>
              <div className="ml-3" data-id="element-2967">
                <div className="text-sm font-medium" data-id="element-2968">Review & Submit</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-2969">
            <div data-id="element-2970">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2971">
                Bill Information
              </h2>
              <p className="text-gray-600" data-id="element-2972">
                Review the bill details for financing
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-2973">
              <div className="grid grid-cols-2 gap-4" data-id="element-2974">
                <div data-id="element-2975">
                  <div className="text-sm text-gray-500" data-id="element-2976">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2977">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-2978">
                  <div className="text-sm text-gray-500" data-id="element-2979">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2980">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-2981">
                  <div className="text-sm text-gray-500" data-id="element-2982">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2983">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-2984">
                  <div className="text-sm text-gray-500" data-id="element-2985">Bill Amount</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2986">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2987">
              <div className="flex items-start gap-3" data-id="element-2988">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-2989" />
                <div className="text-sm text-blue-800" data-id="element-2990">
                  <div className="font-medium mb-1" data-id="element-2991">
                    Bill Financing Benefits
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-2992">
                    <li data-id="element-2993">Improve cash flow by paying suppliers early</li>
                    <li data-id="element-2994">Maintain good supplier relationships</li>
                    <li data-id="element-2995">Take advantage of early payment discounts</li>
                    <li data-id="element-2996">Flexible repayment terms</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-2997">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-2998">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-2999">
            <div data-id="element-3000">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3001">
                Select Genie Business Financing Package
              </h2>
              <p className="text-gray-600" data-id="element-3002">
                Choose a financing package that best suits your needs
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-3003">
              <div className="flex items-start gap-3" data-id="element-3004">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3005" />
                <div data-id="element-3006">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-3007">
                    Genie Business - Your Trusted Financial Partner
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-3008">
                    As a valued Genie Business customer, you have access to
                    exclusive financing packages tailored for your business
                    needs.
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-3009">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-3010">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-3011" />
                  <div className="flex items-start justify-between" data-id="element-3012">
                    <div className="flex-1" data-id="element-3013">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3014">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-3015">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-3016" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-3017">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3018">
                        <div data-id="element-3019">
                          <div className="text-gray-500" data-id="element-3020">Interest Rate</div>
                          <div className="font-medium text-gray-900" data-id="element-3021">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-3022">
                          <div className="text-gray-500" data-id="element-3023">Max Term</div>
                          <div className="font-medium text-gray-900" data-id="element-3024">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-3025">
                          <div className="text-gray-500" data-id="element-3026">Processing Time</div>
                          <div className="font-medium text-gray-900" data-id="element-3027">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-3028">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3029">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3030">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-3031">
            <div data-id="element-3032">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3033">
                Financing Terms
              </h2>
              <p className="text-gray-600" data-id="element-3034">
                Specify the financing amount and terms
              </p>
            </div>
            <div className="space-y-6" data-id="element-3035">
              <div data-id="element-3036">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3037">
                  Financing Amount (LKR) *
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder="Enter amount" max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3038" />
                <div className="mt-1 text-sm text-gray-500" data-id="element-3039">
                  Maximum: Rs. {bill.amount.toLocaleString('en-LK')}
                </div>
              </div>
              <div data-id="element-3040">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3041">
                  Requested Term (Days) *
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3042">
                  <option value="15" data-id="element-3043">15 days</option>
                  <option value="30" data-id="element-3044">30 days</option>
                  <option value="45" data-id="element-3045">45 days</option>
                  <option value="60" data-id="element-3046">60 days</option>
                  <option value="90" data-id="element-3047">90 days</option>
                </select>
              </div>
              <div data-id="element-3048">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3049">
                  Purpose of Financing *
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3050">
                  <option value="" data-id="element-3051">Select purpose</option>
                  <option value="early_payment" data-id="element-3052">Early Payment Discount</option>
                  <option value="cash_flow" data-id="element-3053">Cash Flow Management</option>
                  <option value="supplier_relationship" data-id="element-3054">
                    Supplier Relationship
                  </option>
                  <option value="other" data-id="element-3055">Other</option>
                </select>
              </div>
              <div data-id="element-3056">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3057">
                  Additional Notes
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder="Any additional information..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-3058" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3059">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3060">
                Back
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3061">
                Review Request
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-3062">
            <div data-id="element-3063">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3064">
                Review Financing Request
              </h2>
              <p className="text-gray-600" data-id="element-3065">
                Please review your financing request before submitting
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-3066">
              <div data-id="element-3067">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3068">
                  BILL INFORMATION
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-3069">
                  <div data-id="element-3070">
                    <div className="text-sm text-gray-500" data-id="element-3071">Bill Number</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3072">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-3073">
                    <div className="text-sm text-gray-500" data-id="element-3074">Supplier</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3075">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-3076">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-3077">
                  FINANCING DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-3078">
                  <div data-id="element-3079">
                    <div className="text-sm text-gray-500" data-id="element-3080">Bank</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3081">
                      Genie Business
                    </div>
                  </div>
                  <div data-id="element-3082">
                    <div className="text-sm text-gray-500" data-id="element-3083">Package</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3084">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-3085">
                    <div className="text-sm text-gray-500" data-id="element-3086">Interest Rate</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3087">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-3088">
                    <div className="text-sm text-gray-500" data-id="element-3089">Processing Time</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3090">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-3091">
                    <div className="text-sm text-gray-500" data-id="element-3092">
                      Financing Amount
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3093">
                      Rs.{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-LK')}
                    </div>
                  </div>
                  <div data-id="element-3094">
                    <div className="text-sm text-gray-500" data-id="element-3095">Term</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-3096">
                      {formData.requestedTerm} days
                    </div>
                  </div>
                  <div data-id="element-3097">
                    <div className="text-sm text-gray-500" data-id="element-3098">Purpose</div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-3099">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-3100">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-3101">
                    ADDITIONAL NOTES
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-3102">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3103">
              <div className="flex items-start gap-3" data-id="element-3104">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3105" />
                <div className="text-sm text-blue-800" data-id="element-3106">
                  By submitting this request, you agree to the financing terms
                  and conditions set by Genie Business for the{' '}
                  {selectedPackage?.name}. The request will be processed within{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-3107">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3108">
                Back
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-3109">
                Submit Financing Request
              </button>
            </div>
          </div>}
      </div>
    </div>;
}